/**
 * Presets de adiamento (spec 4.3): +1 h, hoje a noite, amanha, proxima semana.
 *
 * Roda no SERVIDOR, e nao no cliente, por um motivo: os presets sao definidos
 * em hora de parede ("19:00", "seg 09:00") e o resultado precisa ser o mesmo
 * quer a pessoa esteja no celular em Belem ou no notebook em outro fuso. O
 * fuso autoritativo e `users.timezone`, nao o do dispositivo.
 */

import { SNOOZE_ANCHOR_HOURS, type SnoozePreset } from './constants.js';
import { addDaysInZone, atHourInZone, partsInZone } from './datetime.js';

const ONE_HOUR_MS = 3_600_000;
const MONDAY = 1; // partsInZone: 0 = domingo

/**
 * Resolve o preset para um instante UTC.
 *
 * Invariante: o resultado e SEMPRE estritamente futuro. "Hoje a noite" as 21h
 * nao pode devolver as 19h do mesmo dia — adiar para o passado deixaria a
 * tarefa vencida no instante do clique, que e o oposto do que o botao promete.
 * Nesse caso o preset rola para o dia seguinte.
 */
export function resolveSnoozePreset(
  preset: SnoozePreset,
  now: number,
  timeZone: string,
): number {
  switch (preset) {
    case 'one_hour':
      return now + ONE_HOUR_MS;

    case 'this_evening': {
      const tonight = atHourInZone(timeZone, now, SNOOZE_ANCHOR_HOURS.thisEveningHour);
      if (tonight > now) return tonight;
      return atHourInZone(
        timeZone,
        addDaysInZone(timeZone, now, 1),
        SNOOZE_ANCHOR_HOURS.thisEveningHour,
      );
    }

    case 'tomorrow':
      return atHourInZone(
        timeZone,
        addDaysInZone(timeZone, now, 1),
        SNOOZE_ANCHOR_HOURS.tomorrowHour,
      );

    case 'next_week':
      return atHourInZone(
        timeZone,
        addDaysInZone(timeZone, now, daysUntilNextMonday(timeZone, now)),
        SNOOZE_ANCHOR_HOURS.nextWeekHour,
      );

    default: {
      // `preset` e `never` aqui: o switch cobre SNOOZE_PRESETS inteiro. Se um
      // preset novo for adicionado a constante, esta linha para de compilar —
      // que e exatamente onde o erro deve aparecer.
      const exhaustive: never = preset;
      throw new Error(`preset de adiamento desconhecido: ${String(exhaustive)}`);
    }
  }
}

/**
 * Dias ate a proxima segunda-feira, sempre >= 1.
 *
 * Numa segunda-feira o resultado e 7, nao 0: "proxima semana" clicado na
 * segunda significa a segunda seguinte, nao daqui a zero dia.
 */
function daysUntilNextMonday(timeZone: string, now: number): number {
  const weekday = partsInZone(timeZone, now).weekday;
  const delta = (MONDAY - weekday + 7) % 7;
  return delta === 0 ? 7 : delta;
}
